import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { ParentNotFoundError } from "@seneca/parents-service-client";

import { getClient } from "services";

import parentAnalytics from "seneca-common/features/parents/features/parent/analytics";

import { ChildrenType, ParentType } from "./types";

export const ParentChildConnectionsQueryKey = "child-connections";

export function useFetchChildConnections<TData>(
  options?: UseQueryOptions<ParentType, unknown, TData>
) {
  return useQuery<ParentType, unknown, TData>(
    [ParentChildConnectionsQueryKey],
    async () => {
      let parent: ParentType | undefined;
      try {
        parent = (await getClient("parentsService").fetchParent({
          includePending: true
        })) as ParentType;
      } catch (err) {
        if (err instanceof ParentNotFoundError) {
          parent = (await getClient(
            "parentsService"
          ).createParent()) as ParentType;
        } else {
          throw err;
        }
      }

      parentAnalytics(parent as ParentType);

      return parent as ParentType;
    },
    {
      staleTime: Infinity,
      ...(options || {})
    }
  );
}

function useQuerySelector<TData>(
  selector: (data: ParentType) => any,
  otherOptions?: UseQueryOptions<ParentType, unknown, TData>
) {
  return useFetchChildConnections<TData>({
    select: selector,
    ...(otherOptions || {})
  });
}

export function useFetchParentCode(
  options: UseQueryOptions<ParentType, unknown, string> = {}
) {
  return useQuerySelector<string>(parentCodeSelector, options);
}

export function useFetchConnectedChildren(
  options?: UseQueryOptions<ParentType, unknown, ChildrenType[]>
) {
  return useQuerySelector(connectedChildrenSelector, options);
}

export function useParentHasChildren() {
  return useQuerySelector<boolean>(parentHasChildrenSelector);
}

export function useFetchConnectedChildrenIdsWithRefetch(
  refetchInterval: number = 10_000
) {
  return useQuerySelector<string[]>(connectedChildrenIdsSelector, {
    refetchInterval
  });
}

export function useFetchConnectedChildrenIds() {
  return useQuerySelector<string[]>(connectedChildrenIdsSelector);
}

export function useFetchPendingChildConnections() {
  return useQuerySelector<ChildrenType[]>(pendingConnectionsSelector);
}

export function useFetchChildConnection(userId: string) {
  return useQuerySelector<ChildrenType | undefined>(
    makeChildConnectionByIdSelector(userId)
  );
}

export function useFetchConnectedChild(
  userId: string,
  options?: UseQueryOptions<ParentType, unknown, ChildrenType | undefined>
) {
  return useQuerySelector(makeConnectedChildSelector(userId), options);
}

const parentCodeSelector = (data: ParentType) => data.code;

const connectedChildrenSelector = (data: ParentType) =>
  data.children.filter(child => child.status === "connected");

const connectedChildrenIdsSelector = (data: ParentType) => {
  const connectedChildren = connectedChildrenSelector(data);
  return connectedChildren.map(child => child.userId);
};

const parentHasChildrenSelector = (data: ParentType) => {
  const connectedChildren = connectedChildrenSelector(data);
  return connectedChildren.length > 0;
};

const makeConnectedChildSelector = (userId: string) => (data: ParentType) => {
  const connectedChildren = connectedChildrenSelector(data);
  return connectedChildren.find(child => child.userId === userId);
};

const pendingConnectionsSelector = (data: ParentType) =>
  data.children.filter(child => child.status === "pending");

const makeChildConnectionByIdSelector =
  (userId: string) => (data: ParentType) =>
    data.children.find(child => child.userId === userId);
